import type { MantineTheme } from '@mantine/core';
import { Center, createStyles, Loader } from '@mantine/core';

import { showNotification } from '@mantine/notifications';
import { EntityType } from '@repo/common/enums/entityType';
import { Button, IconButton, ListBox, Text } from '@repo/foundations';
import type { IconButtonProps } from '@repo/foundations/components/Buttons/IconButton';
import { size } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import {
	useMention,
	type ISecodaEntity,
	type SearchResult,
} from '../../../../api';
import {
	invalidateResourceRelationList,
	useCreateResourceRelation,
} from '../../../../api/hooks/relations';
import type { SecodaEntity } from '../../../../lib/models';
import { ResourceLinkType } from '../../../Link/useResourceLink';
import { ResourcePreview } from '../../../ResourcePreview';
import type { EntityPreview } from '../../../ResourcePreview/ResourcePreview.types';
import { RelatedResourceAIChatItem } from './RelatedResourceAIChatItem';
import RelatedResourceItem from './RelatedResourceItem';

interface RelatedResourceDropdownProps extends Partial<IconButtonProps> {
	entity: SecodaEntity | ISecodaEntity;
	relatedEntityIds: string[];
}

const useStyles = createStyles((theme: MantineTheme) => ({
	emptyState: {
		padding: theme.spacing['2xs'],
	},
}));

const extractUUID = (text: string) => {
	const uuidRegex =
		/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
	const match = text.match(uuidRegex);
	return match ? match[0] : null;
};

const checkLinkToAI = (text: string) => {
	const aiLinkRegex =
		/ai\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;
	return aiLinkRegex.test(text);
};

export function RelatedResourceCreateMenu({
	entity,
	relatedEntityIds,
	...iconButtonProps
}: RelatedResourceDropdownProps) {
	const { mutateAsync: createResourceRelation } = useCreateResourceRelation({
		options: {
			onSuccess: () => {
				showNotification({
					message: 'Related resource added.',
					color: 'green',
				});
				invalidateResourceRelationList(entity.id);
				setOpened(false);
			},
			onError: () => {
				showNotification({
					message: 'Failed to add a related resource',
					color: 'red',
				});
			},
		},
	});

	const handleCreateResourceRelation = useCallback(
		(selected: SearchResult) => {
			createResourceRelation({
				data: {
					from_entity: entity.id,
					to_entity: selected.id,
				},
			});
		},
		[createResourceRelation, entity.id]
	);

	const [opened, setOpened] = useState<boolean>(false);

	const [searchTerm, setSearchTerm] = useState('');
	const [AILinkProvided, setAILinkProvided] = useState(false);

	const { classes, theme } = useStyles();

	const handleSearchChange = (value: string) => {
		setSearchTerm(value);
		setAILinkProvided(checkLinkToAI(value.trim()));
	};
	const { isFetching, data } = useMention({
		searchTerm: searchTerm,
		filters: {
			operator: 'and',
			operands: [
				{
					field: 'entity_type',
					operator: 'in',
					value: Object.values(EntityType).filter(
						(el) =>
							![
								EntityType.user,
								EntityType.user_group,
								EntityType.question_reply,
								EntityType.event_property,
								EntityType.collection,
							].includes(el)
					),
					operands: [],
				},
				{
					operator: 'not',
					operands: [
						{
							operands: [],
							operator: 'in',
							field: 'id',
							value: [...relatedEntityIds, entity.id],
						},
					],
				},
			],
		},
		options: {
			select: (data) => data.results,
		},
	});

	const height =
		size(data) > 6 ? theme.other.space[60] : size(data) * theme.other.space[12];

	const handleOpenChange = useCallback(
		(newOpened: boolean) => {
			setSearchTerm('');
			setOpened(newOpened);
		},
		[setSearchTerm]
	);

	const items: (SearchResult | { id: 'ai' })[] | undefined = useMemo(() => {
		if (AILinkProvided && entity.entity_type === EntityType.question) {
			return [
				{
					id: 'ai',
				},
				...(data || []),
			];
		}
		return data;
	}, [AILinkProvided, entity.entity_type, data]);

	const isEmpty = relatedEntityIds.length === 0;

	return (
		<ListBox opened={opened} onOpenChange={handleOpenChange} closeOnEscape>
			<ListBox.Target>
				{isEmpty ? (
					<Button
						leftIconName="plus"
						variant="tertiary"
						iconColor="icon/secondary/default"
					>
						<Text size="sm" color="text/secondary/default">
							Add related
						</Text>
					</Button>
				) : (
					<IconButton
						iconName="plus"
						variant="tertiary"
						tooltip="Add related resource"
						{...iconButtonProps}
					/>
				)}
			</ListBox.Target>
			<ListBox.ItemsDropdown
				usePortal
				search={{
					onChange: handleSearchChange,
					value: searchTerm,
					placeholder: 'Search or add link',
				}}
				items={items}
				renderItem={(item, getProps) => {
					if (item.id === 'ai') {
						return (
							<RelatedResourceAIChatItem
								promptId={extractUUID(searchTerm) || ''}
								entityId={entity.id}
							/>
						);
					} else {
						return (
							<ResourcePreview
								id={item.id}
								data={
									{
										...item,
										archived: false,
										stale: false,
									} as EntityPreview
								}
								resourceType={ResourceLinkType.Entity}
								isLoading={false}
								hasError={false}
								openDelay={200}
								closeDelay={50}
								withinPortal
								position="right-start"
								disabled={false}
							>
								<div>
									<RelatedResourceItem
										item={item as SearchResult}
										onClick={handleCreateResourceRelation}
										isViewerUser={false}
										key={item.id}
										getProps={getProps}
									/>
								</div>
							</ResourcePreview>
						);
					}
				}}
			>
				{isFetching && (
					<Center h={height} w={theme.other.space[60]}>
						<Loader size="sm" />
					</Center>
				)}
			</ListBox.ItemsDropdown>
		</ListBox>
	);
}
